/**
 * Brand accent solver, shared by the league admin (admin-v2) and the public
 * site's website templates (site templates §7.4). Extracted verbatim from
 * `admin-v2/theme/brandAccent.ts`; that module now wraps `deriveAccentFor`
 * with the admin's own surfaces and keeps its public API unchanged.
 *
 * The league's PRIMARY colour drives exactly one family of tokens: primary
 * buttons, the active nav item, selection, focus rings and links. Neutrals
 * and status colours are fixed by whoever owns the surfaces (the admin's
 * tokens.css, a template's styles.css) and never come from the tenant.
 *
 * `deriveAccentFor(primaryHex, { mode, grounds })` is pure: same input, same
 * output, no DOM. It contrast-corrects the tenant colour for the given
 * grounds (the surfaces the accent must work on):
 *
 *   accent      button fill / active-nav bar / selected checkbox. Visible on
 *               every ground; carries `onAccent` text at >= 4.5:1.
 *   accentHover hover/pressed fill. Still >= 4.5:1 with `onAccent`.
 *   onAccent    text/icon colour on `accent` (white or the dark ink).
 *   accentText  links and accent-coloured text on any ground, including
 *               `accentSoft`: >= 4.5:1 (WCAG 1.4.3).
 *   accentSoft  quiet tint for selected rows, active nav item, info chips.
 *   focusRing   focus indicator colour: >= 3:1 against every ground
 *               (WCAG 1.4.11 / 2.4.13 adjacent-colour contrast).
 *
 * Why a separate `accentText`: a bright brand fill (MCL gold #FFD700, H4U
 * orange #F5821F) works as a button with dark text, but can never be a link
 * colour on white. Forcing one colour to do both would turn every gold league
 * mustard-brown. Fills keep the brand; text gets the nearest legible shade.
 *
 * All lightness moves happen in OKLCH at constant hue, so a corrected colour
 * still reads as "their" colour. Chroma is reduced only to stay in the sRGB
 * gamut.
 */

export type AccentMode = 'light' | 'dark'

export type StatusName = 'live' | 'needs' | 'final'

export interface AccentTokens {
  accent: string
  accentHover: string
  onAccent: string
  accentSoft: string
  focusRing: string
  accentText: string
  meta: {
    /** The colour we started from (normalised #rrggbb), or the fallback. */
    source: string
    /** Input was missing/invalid; derived from the fallback instead. */
    fallback: boolean
    /** Input had no usable hue (white, grey, black): monochrome accent. */
    neutral: boolean
    /** True when `accent` differs from the source colour. */
    fillAdjusted: boolean
    /** True when `accentText` differs from the source colour. */
    textAdjusted: boolean
    /** The fixed status hue this accent sits near, if any (for QA + soft tint). */
    nearStatus: StatusName | null
    contrast: {
      onAccent: number
      onAccentHover: number
      /** Worst case of accentText over every ground incl. accentSoft. */
      text: number
      /** Worst case of focusRing over every ground. */
      focus: number
    }
  }
}

/** The grounds an accent is solved against. */
export interface AccentSurfaces {
  mode: AccentMode
  /** Every surface the accent appears on (canvas, surface, surface-2, …), as hex. */
  grounds: string[]
}

/**
 * Optional tuning. Every default is the admin-v2 value, so admin output is
 * unchanged; a template may pass its own inks and neutral tints.
 */
export interface AccentOptions {
  /** Used when the primary is missing or can't be parsed. */
  fallback?: string
  /** Dark ink used as on-accent text on bright fills (and the light-mode monochrome accent). */
  onAccentDark?: string
  /** Light on-accent text. */
  onAccentLight?: string
  /** Dark-mode monochrome accent for hue-less primaries. */
  neutralDarkAccent?: string
  /** `accentSoft` for hue-less primaries, per mode. */
  neutralSoft?: Partial<Record<AccentMode, string>>
}

/** Admin ink in light mode: the dark text used on bright accent fills. */
export const ON_ACCENT_DARK = '#1D1A15'
export const ON_ACCENT_LIGHT = '#FFFFFF'

/** Used when the league has no primary colour or it can't be parsed. */
export const DEFAULT_PRIMARY = '#3347D1'

const NEUTRAL_DARK_ACCENT = '#F3EFE7'
const NEUTRAL_SOFT: Record<AccentMode, string> = { light: '#EFECE6', dark: '#2C2822' }

/** Fixed status hues (OKLCH degrees) of admin tokens.css live / needs / final. */
const STATUS_HUES: Record<StatusName, number> = { live: 29, needs: 75, final: 158 }

const AA_TEXT = 4.5
const AA_NON_TEXT = 3
/** Below this OKLCH chroma a colour has no hue worth keeping. */
const NEUTRAL_CHROMA = 0.035
/** Light mode: a fill must differ from the paper by this OKLab distance. */
const LIGHT_FILL_MIN_DELTA_E = 0.12
/** Dark mode: a fill must reach this contrast against the darkest surface. */
const DARK_FILL_MIN_CONTRAST = 2.2
/** Prefer white text unless it costs this much more lightness than ink. */
const WHITE_TEXT_BIAS = 0.1
const HOVER_STEP = 0.05
const NEAR_STATUS_DEG = 22

// ── Colour maths ─────────────────────────────────────────────────────────

type RGB = [number, number, number] // 0..1 sRGB (gamma encoded)
export interface LCH {
  L: number
  C: number
  h: number
}

export function parseHex(input: string | null | undefined): string | null {
  if (typeof input !== 'string') return null
  const m = /^\s*#?([0-9a-f]{3}|[0-9a-f]{6})\s*$/i.exec(input)
  if (!m) return null
  let h = m[1].toLowerCase()
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return `#${h.toUpperCase()}`
}

function hexToRgb(hex: string): RGB {
  const n = parseInt(hex.slice(1), 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

function rgbToHex([r, g, b]: RGB): string {
  const to = (v: number) =>
    Math.round(Math.min(1, Math.max(0, v)) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase()
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055)

export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG 2.x contrast ratio, 1..21. */
export function contrast(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

function rgbToOklab(rgb: RGB): [number, number, number] {
  const [r, g, b] = rgb.map(toLinear)
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

/** OKLab → linear sRGB (may be out of gamut). */
function oklabToLinear(L: number, a: number, b: number): RGB {
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3)
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3)
  const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3)
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
}

/** Hex → OKLCH. */
export function hexToLch(hex: string): LCH {
  const [L, a, b] = rgbToOklab(hexToRgb(hex))
  const C = Math.sqrt(a * a + b * b)
  let h = (Math.atan2(b, a) * 180) / Math.PI
  if (h < 0) h += 360
  return { L, C, h }
}

const inGamut = (rgb: RGB) => rgb.every((v) => v >= -1e-4 && v <= 1 + 1e-4)

/** OKLCH → hex, reducing chroma (never hue or lightness) to fit sRGB. */
export function lchToHex({ L, C, h }: LCH): string {
  const Lc = Math.min(1, Math.max(0, L))
  const rad = (h * Math.PI) / 180
  const at = (c: number) => oklabToLinear(Lc, c * Math.cos(rad), c * Math.sin(rad))
  let rgb = at(C)
  if (!inGamut(rgb)) {
    let lo = 0
    let hi = C
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2
      if (inGamut(at(mid))) lo = mid
      else hi = mid
    }
    rgb = at(lo)
  }
  return rgbToHex(rgb.map((v) => toGamma(Math.min(1, Math.max(0, v)))) as RGB)
}

function deltaE(a: string, b: string): number {
  const [L1, a1, b1] = rgbToOklab(hexToRgb(a))
  const [L2, a2, b2] = rgbToOklab(hexToRgb(b))
  return Math.hypot(L1 - L2, a1 - a2, b1 - b2)
}

const hueDistance = (a: number, b: number) => {
  const d = Math.abs(a - b) % 360
  return d > 180 ? 360 - d : d
}

/**
 * Move lightness from `start` toward `target` (0 = darken, 1 = lighten) and
 * return the colour closest to the start that satisfies `ok`. Scans in small
 * steps (the predicate may pass only inside a window, e.g. "readable with
 * white text AND still visible on a dark surface"), then bisects the edge.
 * Returns the start colour if it already passes; `passed: false` and the
 * start colour if no lightness on that side passes.
 */
export function solveLightness(
  start: LCH,
  target: 0 | 1,
  ok: (hex: string) => boolean,
): { hex: string; L: number; passed: boolean } {
  const startHex = lchToHex(start)
  if (ok(startHex)) return { hex: startHex, L: start.L, passed: true }
  const dir = target === 0 ? -1 : 1
  const STEP = 0.01
  let prev = start.L
  for (let L = start.L + dir * STEP; dir < 0 ? L >= -1e-9 : L <= 1 + 1e-9; L += dir * STEP) {
    if (ok(lchToHex({ ...start, L }))) {
      let fail = prev
      let pass = L
      for (let i = 0; i < 20; i++) {
        const mid = (fail + pass) / 2
        if (ok(lchToHex({ ...start, L: mid }))) pass = mid
        else fail = mid
      }
      return { hex: lchToHex({ ...start, L: pass }), L: pass, passed: true }
    }
    prev = L
  }
  return { hex: startHex, L: start.L, passed: false }
}

/** Lowest WCAG contrast of `fg` over every background. */
export const minContrast = (fg: string, bgs: string[]) => Math.min(...bgs.map((bg) => contrast(fg, bg)))

// ── The pipeline ─────────────────────────────────────────────────────────

export function deriveAccentFor(
  primaryHex: string | null | undefined,
  { mode, grounds }: AccentSurfaces,
  opts: AccentOptions = {},
): AccentTokens {
  const onDark = opts.onAccentDark ?? ON_ACCENT_DARK
  const onLight = opts.onAccentLight ?? ON_ACCENT_LIGHT
  const neutralDarkAccent = opts.neutralDarkAccent ?? NEUTRAL_DARK_ACCENT
  const neutralSoft = { ...NEUTRAL_SOFT, ...opts.neutralSoft }
  const parsed = parseHex(primaryHex)
  const source = parsed ?? parseHex(opts.fallback) ?? DEFAULT_PRIMARY
  const surfaces = grounds
  const base = hexToLch(source)
  const neutral = base.C < NEUTRAL_CHROMA

  let nearStatus: StatusName | null = null
  if (!neutral && base.C >= 0.08) {
    for (const [name, hue] of Object.entries(STATUS_HUES) as [StatusName, number][]) {
      if (hueDistance(base.h, hue) <= NEAR_STATUS_DEG) nearStatus = name
    }
  }

  // 1. Fill visibility floor.
  const fillVisible = (hex: string) =>
    mode === 'light'
      ? surfaces.every((bg) => deltaE(hex, bg) >= LIGHT_FILL_MIN_DELTA_E)
      : minContrast(hex, surfaces) >= DARK_FILL_MIN_CONTRAST

  let fillStart: LCH
  if (neutral) {
    // No hue to keep: a monochrome accent (ink in light, paper in dark).
    fillStart = hexToLch(mode === 'light' ? onDark : neutralDarkAccent)
  } else {
    // Light mode: pale fills get darker. Dark mode: dim fills get lighter.
    fillStart = { ...base, L: solveLightness(base, mode === 'light' ? 0 : 1, fillVisible).L }
  }

  // 2. Pick on-accent text: white (darken fill) or ink (lighten fill).
  const whiteOk = (hex: string) => contrast(onLight, hex) >= AA_TEXT && fillVisible(hex)
  const inkOk = (hex: string) => contrast(onDark, hex) >= AA_TEXT && fillVisible(hex)
  const asWhite = solveLightness(fillStart, 0, whiteOk)
  const asInk = solveLightness(fillStart, 1, inkOk)
  const whitePasses = asWhite.passed
  const inkPasses = asInk.passed
  const costWhite = Math.abs(fillStart.L - asWhite.L)
  const costInk = Math.abs(asInk.L - fillStart.L)

  let useWhite: boolean
  if (whitePasses && inkPasses) useWhite = costWhite <= costInk + WHITE_TEXT_BIAS
  else useWhite = whitePasses || !inkPasses
  const onAccent = useWhite ? onLight : onDark
  const accent = useWhite ? asWhite.hex : asInk.hex
  const accentL = useWhite ? asWhite.L : asInk.L

  // 3. Hover: darker in light mode, lighter in dark mode (the direction
  //    users expect), unless that would drop below AA with `onAccent`, in
  //    which case it steps the other way (which only increases contrast).
  const accentLch = { ...hexToLch(accent), L: accentL }
  const preferred = mode === 'light' ? -HOVER_STEP : HOVER_STEP
  let accentHover = lchToHex({ ...accentLch, L: clamp01(accentL + preferred) })
  if (contrast(onAccent, accentHover) < AA_TEXT) {
    accentHover = lchToHex({ ...accentLch, L: clamp01(accentL - preferred) })
  }

  // 4. Soft tint (selection, active nav, info chips).
  let accentSoft: string
  if (neutral) {
    accentSoft = neutralSoft[mode]
  } else {
    const chromaScale = nearStatus ? 0.5 : 1
    accentSoft =
      mode === 'light'
        ? lchToHex({ L: 0.955, C: Math.min(base.C * 0.3, 0.045) * chromaScale, h: base.h })
        : lchToHex({ L: 0.3, C: Math.min(base.C * 0.4, 0.06) * chromaScale, h: base.h })
  }

  // 5. Accent text (links): legible on every surface AND on accentSoft.
  const textBgs = [...surfaces, accentSoft]
  const textStart = neutral ? hexToLch(mode === 'light' ? onDark : neutralDarkAccent) : base
  const accentText = solveLightness(textStart, mode === 'light' ? 0 : 1, (hex) => minContrast(hex, textBgs) >= AA_TEXT).hex

  // 6. Focus ring: the brand fill if it reaches 3:1 on every surface, else the text shade.
  const focusRing = minContrast(accent, surfaces) >= AA_NON_TEXT ? accent : accentText

  return {
    accent,
    accentHover,
    onAccent,
    accentSoft,
    focusRing,
    accentText,
    meta: {
      source,
      fallback: parsed === null,
      neutral,
      fillAdjusted: accent !== source,
      textAdjusted: accentText !== source,
      nearStatus,
      contrast: {
        onAccent: round2(contrast(onAccent, accent)),
        onAccentHover: round2(contrast(onAccent, accentHover)),
        text: round2(minContrast(accentText, textBgs)),
        focus: round2(minContrast(focusRing, surfaces)),
      },
    },
  }
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n))
const round2 = (n: number) => Math.round(n * 100) / 100

/** "#RRGGBB" → "r g b" channels, for `rgb(var(--x) / <alpha>)` in Tailwind. */
export function hexToChannels(hex: string): string {
  const [r, g, b] = hexToRgb(hex).map((v) => Math.round(v * 255))
  return `${r} ${g} ${b}`
}
